/**
 * Federal court reference data for the Judicial Clerkship EPA (2026).
 *
 * Shared across apps. Sourced from OARM's clerkship spec (Tab A / Tab B). Note:
 * the source doc's "Twelfth Circuit" was dropped (no such circuit exists — the
 * federal courts of appeals are the 1st–11th, D.C., and Federal Circuit), and
 * Tab B is enumerated in full (the doc's "24" count was inaccurate).
 */

/** U.S. Courts of Appeals (13). */
export const FEDERAL_CIRCUIT_COURTS: readonly string[] = [
	'U.S. Court of Appeals for the First Circuit',
	'U.S. Court of Appeals for the Second Circuit',
	'U.S. Court of Appeals for the Third Circuit',
	'U.S. Court of Appeals for the Fourth Circuit',
	'U.S. Court of Appeals for the Fifth Circuit',
	'U.S. Court of Appeals for the Sixth Circuit',
	'U.S. Court of Appeals for the Seventh Circuit',
	'U.S. Court of Appeals for the Eighth Circuit',
	'U.S. Court of Appeals for the Ninth Circuit',
	'U.S. Court of Appeals for the Tenth Circuit',
	'U.S. Court of Appeals for the Eleventh Circuit',
	'U.S. Court of Appeals for the District of Columbia Circuit',
	'U.S. Court of Appeals for the Federal Circuit',
];

/**
 * The 94 U.S. District Courts (Tab A). Values are the district names; the
 * "U.S. District Court - " prefix is applied at render/persist time so the
 * Employment "Employer" field reads e.g. "U.S. District Court - Alaska".
 */
export const US_DISTRICT_COURTS: readonly string[] = [
	'Alabama, Northern',
	'Alabama, Middle',
	'Alabama, Southern',
	'Alaska',
	'Arizona',
	'Arkansas, Eastern',
	'Arkansas, Western',
	'California, Northern',
	'California, Eastern',
	'California, Central',
	'California, Southern',
	'Colorado',
	'Connecticut',
	'Delaware',
	'District of Columbia',
	'Florida, Northern',
	'Florida, Middle',
	'Florida, Southern',
	'Georgia, Northern',
	'Georgia, Middle',
	'Georgia, Southern',
	'Guam',
	'Hawaii',
	'Idaho',
	'Illinois, Northern',
	'Illinois, Central',
	'Illinois, Southern',
	'Indiana, Northern',
	'Indiana, Southern',
	'Iowa, Northern',
	'Iowa, Southern',
	'Kansas',
	'Kentucky, Eastern',
	'Kentucky, Western',
	'Louisiana, Eastern',
	'Louisiana, Middle',
	'Louisiana, Western',
	'Maine',
	'Maryland',
	'Massachusetts',
	'Michigan, Eastern',
	'Michigan, Western',
	'Minnesota',
	'Mississippi, Northern',
	'Mississippi, Southern',
	'Missouri, Eastern',
	'Missouri, Western',
	'Montana',
	'Northern Mariana Islands',
	'Nebraska',
	'Nevada',
	'New Hampshire',
	'New Jersey',
	'New Mexico',
	'New York, Northern',
	'New York, Eastern',
	'New York, Southern',
	'New York, Western',
	'North Carolina, Eastern',
	'North Carolina, Middle',
	'North Carolina, Western',
	'North Dakota',
	'Ohio, Northern',
	'Ohio, Southern',
	'Oklahoma, Northern',
	'Oklahoma, Eastern',
	'Oklahoma, Western',
	'Oregon',
	'Pennsylvania, Eastern',
	'Pennsylvania, Middle',
	'Pennsylvania, Western',
	'Puerto Rico',
	'Rhode Island',
	'South Carolina',
	'South Dakota',
	'Tennessee, Eastern',
	'Tennessee, Middle',
	'Tennessee, Western',
	'Texas, Northern',
	'Texas, Eastern',
	'Texas, Southern',
	'Texas, Western',
	'Utah',
	'Vermont',
	'Virgin Islands',
	'Virginia, Eastern',
	'Virginia, Western',
	'Washington, Eastern',
	'Washington, Western',
	'West Virginia, Northern',
	'West Virginia, Southern',
	'Wisconsin, Eastern',
	'Wisconsin, Western',
	'Wyoming',
];

/** Prefix applied to a U.S. District Court selection for the Employer field. */
export const US_DISTRICT_COURT_PREFIX = 'U.S. District Court - ';

/** An "Other Federal Court" option (Tab B). */
export interface OtherFederalCourt {
	name: string;
	/**
	 * True for DOJ-affiliated tribunals whose clerkships post to the Employment
	 * screen under "Prior DOJ Experience" rather than as outside employment.
	 */
	isDoj: boolean;
}

/**
 * Other Federal Courts (Tab B), including agency administrative-law tribunals.
 * DOJ-affiliated entries are flagged so their clerkships route to DOJ
 * Experience on the Employment screen.
 *
 * Declared in the order OARM's source document lists them; the exported
 * {@link OTHER_FEDERAL_COURTS} is sorted alphabetically for display. Add new
 * entries anywhere here — sorting is applied at export, so the dropdown stays in
 * order without anyone having to find the right insertion point.
 */
const OTHER_FEDERAL_COURTS_SOURCE: readonly OtherFederalCourt[] = [
	{ name: 'United States Tax Court', isDoj: false },
	{ name: 'Patent Trial and Appeal Board', isDoj: false },
	{ name: 'International Trade Commission', isDoj: false },
	{ name: 'United States Court of International Trade', isDoj: false },
	{ name: 'United States Court of Federal Claims', isDoj: false },
	{ name: 'United States Foreign Intelligence Surveillance Court', isDoj: false },
	{ name: 'United States Bankruptcy Court', isDoj: false },
	{ name: 'Trademark Trial and Appeal Board', isDoj: false },
	{ name: 'United States Merit Systems Protection Board', isDoj: false },
	{ name: 'United States Alien Terrorist Removal Court', isDoj: false },
	{ name: 'United States Court of Appeals for the Armed Forces', isDoj: false },
	{ name: 'United States Army Court of Criminal Appeals', isDoj: false },
	{ name: 'Navy-Marine Corps Court of Criminal Appeals', isDoj: false },
	{ name: 'Air Force Court of Criminal Appeals', isDoj: false },
	{ name: 'Coast Guard Court of Criminal Appeals', isDoj: false },
	{ name: 'United States Court of Appeals for Veterans Claims', isDoj: false },
	{ name: 'United States Foreign Intelligence Surveillance Court of Review', isDoj: false },
	{ name: 'United States Court of Military Commission Review', isDoj: false },
	{ name: 'Civilian Board of Contract Appeals', isDoj: false },
	{ name: 'Armed Services Board of Contract Appeals', isDoj: false },
	{ name: 'Postal Service Board of Contract Appeals', isDoj: false },
	{ name: 'Office of Dispute Resolution for Acquisition', isDoj: false },
	{ name: 'DOJ - Board of Immigration Appeals', isDoj: true },
	{ name: "Board of Veterans' Appeals", isDoj: false },
	{ name: 'DOJ - Office of the Chief Immigration Judge', isDoj: true },
	{ name: 'DOJ - Drug Enforcement Administration Administrative Law Judges', isDoj: true },
	{ name: 'Executive Branch Agency Administrative Law Clerkship', isDoj: false },
];

/**
 * Other Federal Courts in alphabetical order, per OARM's request — the list is
 * long enough that source order made an applicant scan the whole thing.
 *
 * Sorting is display-only and safe for stored data: a clerkship persists the
 * court's NAME, never its position in this array.
 */
export const OTHER_FEDERAL_COURTS: readonly OtherFederalCourt[] = [...OTHER_FEDERAL_COURTS_SOURCE].sort((a, b) => a.name.localeCompare(b.name, 'en'));
